import type { Route } from "./+types/academy.$slug";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { detectLocale, translations } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta, articleJsonLd, breadcrumbJsonLd, SITE_URL } from "~/lib/seo";
import { getArticleBySlug, getRelatedArticles } from "@caratzon/server";
import { renderMarkdown } from "~/lib/markdown.server";
import { Link, useLoaderData } from "react-router";
import { Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { resolveRenderUrl } from "~/lib/utils";

export async function loader({ params, request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  const article = await getArticleBySlug(params.slug);
  if (!article) {
    throw new Response("Not Found", { status: 404 });
  }

  const htmlContent = renderMarkdown(article.body);
  const related = await getRelatedArticles(params.slug, 3);

  return { locale, article, htmlContent, related };
}

export function meta({ data }: Route.MetaArgs) {
  if (!data?.article) return [{ title: "Not Found — Caratzon" }];
  const { article, locale } = data;

  const ogImageUrl = article.ogImage
    ? `${SITE_URL}/resources/assets/render?key=${encodeURIComponent(article.ogImage)}`
    : undefined;

  return [
    { title: `${article.title} — Caratzon Academy` },
    { name: "description", content: article.excerpt },
    ...socialMeta({
      title: article.title,
      description: article.excerpt,
      pathname: `/academy/${article.slug}`,
      locale,
      image: ogImageUrl,
    }),
    { property: "og:type", content: "article" },
    {
      property: "article:published_time",
      content: article.publishedAt ? new Date(article.publishedAt).toISOString() : "",
    },
    { property: "article:section", content: article.category },
    {
      "script:ld+json": JSON.stringify(
        articleJsonLd({
          title: article.title,
          description: article.excerpt,
          slug: article.slug,
          authorName: article.authorName,
          publishedAt: article.publishedAt ? new Date(article.publishedAt).toISOString() : "",
          updatedAt: new Date(article.updatedAt).toISOString(),
          image: ogImageUrl,
        }),
      ),
    },
    {
      "script:ld+json": JSON.stringify(
        breadcrumbJsonLd([
          { name: "Home", url: SITE_URL },
          { name: "Academy", url: `${SITE_URL}/academy` },
          { name: article.title, url: `${SITE_URL}/academy/${article.slug}` },
        ]),
      ),
    },
  ];
}

export default function ArticleDetailPage() {
  const { article, htmlContent, related } = useLoaderData<typeof loader>();
  const { t } = useT();
  const a = t.academy;

  return (
    <MarketingLayout>
      <div className="mx-auto max-w-6xl px-6 py-12">
      <article>
        {/* Back link */}
        <Link
          to="/academy"
          className="mb-10 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> {a.backToAcademy}
        </Link>

        {/* ── Article header ── */}
        <header className="mb-12">
          <div className="border-t border-foreground/15 mb-6" />
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
              {a.categories[article.category as keyof typeof a.categories]}
            </span>
            <span className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
              {article.publishedAt
                ? new Date(article.publishedAt).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : ""}
            </span>
          </div>

          <h1 className="mt-6 font-display text-5xl md:text-6xl font-light italic tracking-tight leading-[1.05]">
            {article.title}
          </h1>

          <p className="mt-6 font-display text-xl font-light italic leading-relaxed text-foreground/70 max-w-[65ch]">
            {article.excerpt}
          </p>

          <div className="my-6 border-t border-foreground/10" />

          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {article.authorName}
            <span className="mx-2">·</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" aria-hidden="true" />
              {article.readingTimeMinutes} {a.readingTime}
            </span>
          </p>
        </header>

        {/* ── Hero image ── */}
        {article.ogImage && (
          <div className="mb-12 overflow-hidden bg-muted" style={{ aspectRatio: "16/9" }}>
            <img
              src={resolveRenderUrl(article.ogImage)}
              alt={article.title}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
        )}

        {/* ── Body + Sidebar ── */}
        <div className="grid gap-16 lg:grid-cols-[1fr_280px]">
          {/* Main content — editorial prose */}
          <div
            className="prose prose-xl max-w-none
              prose-headings:font-display prose-headings:tracking-tight prose-headings:font-medium
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-blockquote:border-l-2 prose-blockquote:border-primary
              prose-blockquote:font-display prose-blockquote:text-xl prose-blockquote:font-light
              prose-blockquote:text-foreground/80 prose-blockquote:not-italic
              [&>p:first-child::first-letter]:font-display
              [&>p:first-child::first-letter]:text-5xl
              [&>p:first-child::first-letter]:float-left
              [&>p:first-child::first-letter]:leading-[0.85]
              [&>p:first-child::first-letter]:mr-2
              [&>p:first-child::first-letter]:mt-1.5"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />

          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-8">
              {/* CTA — teal sapphire background */}
              <div className="bg-teal p-6">
                <h3 className="font-display text-lg font-medium text-teal-fg">
                  {a.cta.title}
                </h3>
                <p className="mt-2 text-sm text-teal-fg/80">{a.cta.description}</p>
                <Link
                  to="/designers"
                  className="mt-4 inline-flex w-full items-center justify-center px-4 py-2.5 text-sm font-medium bg-white/20 text-teal-fg transition-colors hover:bg-white/30"
                >
                  {a.cta.button}
                </Link>
              </div>

              {/* Related articles — flat ruled list */}
              {related.length > 0 && (
                <div>
                  <p className="mb-2 text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                    {a.relatedArticles}
                  </p>
                  {related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/academy/${r.slug}`}
                      className="group block border-t border-foreground/10 py-3"
                    >
                      <h4 className="font-display text-sm font-medium line-clamp-2 group-hover:text-teal transition-colors">
                        {r.title}
                      </h4>
                      <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" aria-hidden="true" />
                        {r.readingTimeMinutes} {a.readingTime}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>

      </article>
        {/* ── Footer CTA — full-bleed dark ── */}
        <div className="-mx-6 mt-20 bg-foreground px-6 py-16 text-center text-background md:py-20">
          <h2 className="font-display text-3xl font-light italic md:text-4xl">
            {a.cta.title}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-background/70">{a.cta.description}</p>
          <Link
            to="/designers"
            className="mt-6 inline-flex items-center gap-2 bg-background px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-background/90"
          >
            {a.cta.button} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </MarketingLayout>
  );
}
